import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react'
import { User as SupabaseUser } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { User, UserRole } from '@/types'
import i18n from '@/lib/i18n'
import { APP_DOMAIN, getLoginUrl } from '@/config/domains'

interface AuthContextType {
  user: User | null
  loading: boolean
  setUser: (user: User | null) => void
  refreshUser: () => Promise<void>
  signUp: (email: string, password: string, name: string) => Promise<{ error: Error | null }>
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signInWithMagicLink: (email: string, redirectUrl?: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  resetPassword: (email: string, redirectUrl?: string) => Promise<{ error: Error | null }>
  // Role impersonation (super_admin only)
  impersonatedRole: UserRole | null
  setImpersonatedRole: (role: UserRole | null) => void
  effectiveRole: UserRole
  isImpersonating: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [impersonatedRole, setImpersonatedRole] = useState<UserRole | null>(null)

  // Calculate effective role: impersonated role (if super_admin is impersonating) or actual role
  const effectiveRole: UserRole = impersonatedRole && user?.role === 'super_admin'
    ? impersonatedRole
    : (user?.role || 'client')

  // Whether we're currently impersonating another role
  const isImpersonating = impersonatedRole !== null && user?.role === 'super_admin'

  useEffect(() => {
    let authTimeout: ReturnType<typeof setTimeout> | null = null

    const initializeAuth = async () => {
      // Check for auth tokens in URL hash (magic links, recovery links)
      const hash = window.location.hash
      const hasAuthTokens = hash && (
        hash.includes('access_token') ||
        hash.includes('refresh_token') ||
        hash.includes('error')
      )

      if (hash) {
        const hashParams = new URLSearchParams(hash.substring(1))
        const accessToken = hashParams.get('access_token')
        const type = hashParams.get('type')
        const error = hashParams.get('error')
      }

      if (hasAuthTokens) {

        // Set timeout to prevent infinite loading if token processing fails
        authTimeout = setTimeout(() => {
          setLoading(false)
          // Clear the hash to prevent retry loops
          window.history.replaceState(null, '', window.location.pathname + window.location.search)
        }, 10000)

        // Don't call getSession yet - let onAuthStateChange handle the token exchange
        // The Supabase client will automatically process the tokens from the URL
        return
      }

      // No auth tokens in URL, check existing session
      const { data: { session } } = await supabase.auth.getSession()

      if (session?.user) {
        loadUserData(session.user)
      } else {
        setLoading(false)
      }
    }

    initializeAuth()

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {

      // Clear timeout if auth succeeded
      if (authTimeout) {
        clearTimeout(authTimeout)
        authTimeout = null
      }

      // Only handle SIGNED_IN and SIGNED_OUT events
      // Ignore TOKEN_REFRESHED and INITIAL_SESSION to prevent unnecessary reloads
      if (event === 'SIGNED_IN' && session?.user) {
        // Don't set loading=true here - it causes the Login component to unmount
        // and lose its pendingNavigation state. The Login component will handle
        // navigation once user is set.
        loadUserData(session.user)

        // Clean up URL hash after successful auth
        if (window.location.hash && window.location.hash.includes('access_token')) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search)
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null)
        setLoading(false)
      }
    })

    return () => {
      subscription.unsubscribe()
      if (authTimeout) clearTimeout(authTimeout)
    }
  }, [])

  /**
   * Ensure user has a personal account (agency) if they don't have one.
   * This is called after loading user data to auto-create accounts for new/trial users.
   */
  const ensureUserAccount = async (userId: string): Promise<void> => {
    try {
      // Skip auto-creation if the signup page is handling account setup
      const signupPending = sessionStorage.getItem('framed_signup_pending')
      if (signupPending) {
        return
      }

      // Check if user already has an agency
      const { data: userData } = await supabase
        .from('users')
        .select('agency_id')
        .eq('id', userId)
        .single()

      if (userData?.agency_id) {
        // User already has an account, nothing to do
        return
      }


      // Call database function to create personal account
      const { data: newAgencyId, error } = await supabase.rpc('create_personal_account_for_user', {
        p_user_id: userId
      })

      if (error) {
        console.error('[AuthContext] Failed to create personal account:', error)
        // Don't throw - user can still use the app, just without an account
        return
      }


      // Note: The user's agency_id is updated by the database function,
      // so we need to refresh the user data
    } catch (err) {
      console.error('[AuthContext] Error ensuring user account:', err)
      // Don't throw - this is not a blocking error
    }
  }

  const loadUserData = async (supabaseUser: SupabaseUser) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', supabaseUser.id)
        .single()

      if (error) {
        // User might have been created through Stripe checkout but webhook failed
        // Try to sync user data
        if (error.code === 'PGRST116') {
          const syncResult = await syncStripeUser(supabaseUser)
          if (syncResult) {
            // Check if synced user is disabled
            if (syncResult.is_disabled) {
              await supabase.auth.signOut()
              setUser(null)
              setLoading(false)
              return
            }
            // Ensure user has an account after sync
            await ensureUserAccount(syncResult.id)
            // Refresh user data to get updated agency_id
            const { data: refreshedUser } = await supabase
              .from('users')
              .select('*')
              .eq('id', syncResult.id)
              .single()
            setUser(refreshedUser || syncResult)
            return
          }

          // User exists in auth.users but not in public.users by ID
          // Check if there's an orphaned record with matching email (e.g. admin-created user with wrong ID)
          const { data: emailUser } = await supabase
            .from('users')
            .select('*')
            .eq('email', supabaseUser.email!.toLowerCase())
            .single()

          if (emailUser) {
            // Found orphaned record — link it to the correct auth ID via edge function
            const { error: linkError } = await supabase.functions.invoke('admin-create-user', {
              body: {
                email: supabaseUser.email,
                name: emailUser.name,
                role: emailUser.role,
                agency_id: emailUser.agency_id,
              },
            })
            if (linkError) {
              console.error('[AuthContext] Failed to link user:', linkError)
            }
            // Refetch with correct ID
            const { data: linkedUser } = await supabase
              .from('users')
              .select('*')
              .eq('id', supabaseUser.id)
              .single()
            if (linkedUser) {
              await ensureUserAccount(linkedUser.id)
              const { data: refreshed } = await supabase
                .from('users')
                .select('*')
                .eq('id', linkedUser.id)
                .single()
              setUser(refreshed || linkedUser)
              return
            }
          }

          // No existing record at all — create fresh profile
          const { data: newUser, error: createError } = await supabase
            .from('users')
            .insert({
              id: supabaseUser.id,
              email: supabaseUser.email!,
              name: supabaseUser.email!.split('@')[0],
              role: 'client' as UserRole,
              preferred_language: 'en',
            })
            .select()
            .single()

          if (createError) {
            console.error('[AuthContext] Failed to create user profile:', createError)
            throw error
          }

          // Ensure user has a personal account
          await ensureUserAccount(newUser.id)
          // Refresh to get updated agency_id
          const { data: refreshedNewUser } = await supabase
            .from('users')
            .select('*')
            .eq('id', newUser.id)
            .single()
          setUser(refreshedNewUser || newUser)
          return
        }
        throw error
      }

      // Check if user is disabled
      if (data?.is_disabled) {
        await supabase.auth.signOut()
        setUser(null)
        setLoading(false)
        // Redirect to login with reason
        window.location.href = `${APP_DOMAIN}/login?reason=disabled`
        return
      }

      // Check if user is soft-deleted
      if (data?.is_deleted) {
        await supabase.auth.signOut()
        setUser(null)
        setLoading(false)
        // Redirect to login with reason
        window.location.href = `${APP_DOMAIN}/login?reason=disabled`
        return
      }

      // Ensure user has a personal account if they don't have one
      if (!data.agency_id) {
        await ensureUserAccount(data.id)
        // Refresh user data to get updated agency_id
        const { data: refreshedUser } = await supabase
          .from('users')
          .select('*')
          .eq('id', data.id)
          .single()
        if (refreshedUser) {
          setUser(refreshedUser)
          // Set language preference if available
          if (refreshedUser.preferred_language) {
            i18n.changeLanguage(refreshedUser.preferred_language)
          }
          return
        }
      }

      setUser(data)

      // Set language preference if available
      if (data?.preferred_language) {
        i18n.changeLanguage(data.preferred_language)
      }
    } catch (error) {
      console.error('Error loading user data:', error)
      // Show a helpful message for users stuck in this state
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  // Sync user who was created through Stripe but webhook failed
  const syncStripeUser = async (supabaseUser: SupabaseUser): Promise<User | null> => {
    try {
      // Call our sync endpoint
      const { data, error } = await supabase.functions.invoke('sync-stripe-user', {
        body: { userId: supabaseUser.id, email: supabaseUser.email }
      })

      if (error) {
        console.error('Failed to sync Stripe user:', error)
        return null
      }

      if (data?.user) {
        return data.user
      }

      return null
    } catch (error) {
      console.error('Error syncing Stripe user:', error)
      return null
    }
  }

  const signUp = async (email: string, password: string, name: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      })

      if (error) return { error }

      if (data.user) {
        // Create user profile with default 'client' role
        const { error: profileError } = await supabase.from('users').insert({
          id: data.user.id,
          email: data.user.email!,
          name,
          role: 'client',
          preferred_language: 'en',
        })

        if (profileError) return { error: profileError }
      }

      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      return { error }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const signInWithMagicLink = async (email: string, customRedirectUrl?: string) => {
    try {
      // Use custom redirect URL if provided, otherwise default to dashboard
      const redirectUrl = customRedirectUrl || `${APP_DOMAIN}/dashboard`

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectUrl,
        },
      })

      if (error) {
        console.error('[AuthContext] Magic link error:', error)
      } else {
      }

      return { error }
    } catch (error) {
      console.error('[AuthContext] Magic link exception:', error)
      return { error: error as Error }
    }
  }

  const signOut = async () => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut()

      // Clear user state
      setUser(null)

      // Clear any cached data
      // Force a full page reload to ensure all state is cleared
      // Redirect to app domain login page
      window.location.href = getLoginUrl()
    } catch (error) {
      console.error('Error signing out:', error)
      // Even on error, try to redirect to login
      window.location.href = getLoginUrl()
    }
  }

  const resetPassword = async (email: string, customRedirectUrl?: string) => {
    try {
      // Use custom redirect URL if provided, otherwise default to set-password page
      const redirectUrl = customRedirectUrl || `${APP_DOMAIN}/set-password`

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      })

      if (error) {
        console.error('[AuthContext] Password reset error:', error)
      } else {
      }

      return { error }
    } catch (error) {
      console.error('[AuthContext] Password reset exception:', error)
      return { error: error as Error }
    }
  }

  const refreshUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        await loadUserData(session.user)
      }
    } catch (error) {
      console.error('Error refreshing user data:', error)
    }
  }

  const value = {
    user,
    loading,
    setUser,
    refreshUser,
    signUp,
    signIn,
    signInWithMagicLink,
    signOut,
    resetPassword,
    // Role impersonation
    impersonatedRole,
    setImpersonatedRole,
    effectiveRole,
    isImpersonating,
  }

  // Mark app as ready once auth check completes (for CSS transition)
  useEffect(() => {
    if (!loading) {
      document.getElementById('root')?.classList.add('app-ready')
    }
  }, [loading])

  // Show loading spinner until initial auth check completes
  // This prevents flash of content before auth state is known
  if (loading) {
    return (
      <AuthContext.Provider value={value}>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-framed-yellow-600"></div>
        </div>
      </AuthContext.Provider>
    )
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
